import * as React from 'react';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { Box, Popper, Typography } from '@mui/material';
import { BsFilm, BsImage } from 'react-icons/bs';
import DateFormatter from './DateFormatter';

const convertTime = (date = "", input, format = "hh:mm:ss") => {
  // const converted = DateFormatter.format(date, input)
  // console.log("Converted date:", converted)
  if (!input) return;
  const hh = format.includes("hh") ? input.slice(0, 2) : undefined;
  const mm = format.includes("mm") ? input.slice(2, 4) : undefined;
  const ss = format.includes("ss") ? input.slice(4, 6) : undefined;
  if (hh && mm && ss) return `${hh}:${mm}:${ss}`;
  if (hh && mm) return `${hh}:${mm}`;
  if (mm && ss) return `${mm}:${ss}`;
  if (hh) return `${hh}`;
};

const TimeColumn = ({
  title,
  label = "label",
  value = "value",
  options,
  onClick,
  selected,
  format = "hh:mm:ss",
  date = "",
  timeZone = "Asia/Qatar",
  activeoptions
}) => {
  const selectedRef = React.useRef(null);

  React.useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selected]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ textAlign: 'center', mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ maxHeight: 222, px: 1, overflowY: 'auto' }}>
        {options.map((opt, index) => {
          // Support both object or string
          const isObject = typeof opt === 'object' && opt !== null;
          const itemValue = isObject ? opt[value] : opt;
          const itemLabel = isObject ? opt[label] : convertTime(date, opt.replace('.jpg', ''), format);
          const isSelected = selected === itemValue;
          const active = activeoptions ? activeoptions.indexOf(itemValue) : 1
          const isActive = active > -1;
          return (
            <Box
              key={index}
              ref={isSelected ? selectedRef : null}
              {...(isActive && { onClick: () => onClick(opt) })}
              sx={{
                p: 1,
                textAlign: 'center',
                cursor: isActive ? 'pointer' : '',
                bgcolor: isSelected ? '#ffd80c' : 'transparent',
                color: isActive ? (isSelected ? 'black' : 'inherit') : "#959595",
                borderRadius: 1,
                '&:hover': { bgcolor: isActive ? '#ffd80c' : 'transparent' },
              }}
            >
              {itemLabel}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const hourOptions = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

export default function XDateCalendar({
  imageData,
  title = "",
  onAction = () => { },
  onDateChange = () => { },
  mode = {}
}) {
  const [value, setValue] = React.useState({});
  const [date, setDate] = React.useState(dayjs());
  const [hoveredDate, setHoveredDate] = React.useState(null);
  const [minsecs, setMinSec] = React.useState([]);
  const [selectedMinSec, setSelectedMinSec] = React.useState([]);
  const [isRecent, setIsRecent] = React.useState(true);

  React.useEffect(() => {
    if (!imageData?.currentCvtDateTime || !imageData?.times?.length) return;
    const timeObj = dayjs(imageData?.currentCvtDateTime, "YYYY-MM-DDTHH:mm");
    setDate(timeObj)
    setValue({ date: timeObj, HH: getLastHour(), dateString: timeObj.format('YYYY-MM-DD') });
  }, [imageData])

  React.useEffect(() => {
    if (!imageData?.currentCvtDateTime || !imageData?.times?.length) return;
    const times = imageData?.times || [];
    const filtered = times.filter(time => time?.cvtDateTime?.startsWith(`${value?.dateString}T${value?.HH}`));
    const unique = Array.from(
      new Map(filtered.map(item => [item.HHmm, item])).values()
    );
    setMinSec(unique)
    setSelectedMinSec(unique[unique.length - 1])
    if (isRecent) {
      setIsRecent(false)
    }
  }, [value])

  React.useEffect(() => {
    if (!selectedMinSec) return
    onAction("date", selectedMinSec)
  }, [selectedMinSec])

  const getLastHour = () => {
    const times = imageData?.times || [];
    const newHour = times?.length > 0 ? times[times.length - 1].HHmm.split(":")[0] : '00';
    return newHour;
  }

  const boxStyle = {
    display: 'flex',
    gap: 1,
    alignItems: 'center',
    fontSize: 12,
    color: 'white',
    borderRadius: 1.5,
    p: 0.5,
    px: 2,
    background: 'black',
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ px: 1 }}>
        {title && <Box sx={boxStyle}>
          <Typography fontSize={13} sx={{ px: 0.5 }}>{title}</Typography>
        </Box>}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <DateCalendar
            {...mode}
            disableFuture
            value={date}
            onChange={(newValue) => {
              setMinSec([])
              setValue({ date: newValue, dateString: newValue.format('YYYY-MM-DD'), HH: getLastHour() })
              onDateChange(newValue.format('YYYY-MM-DD'));
            }}
            onMonthChange={(newValue) => {
              const newMonth = newValue.format("YYYY-MM");
              const currentMonth = date.format("YYYY-MM")
              if (newMonth === currentMonth) return;
              setMinSec([])
              setValue({ date: newValue, dateString: newValue.format('YYYY-MM-DD'), HH: getLastHour() })
              onDateChange(newValue.format('YYYY-MM-DD'));
            }
            }
            onYearChange={(newValue) => {
              setMinSec([])
              setValue({ date: newValue, dateString: newValue.format('YYYY-MM-DD'), HH: getLastHour() })
              onDateChange(newValue.format('YYYY-MM-DD'));
            }}
            slotProps={{
              day: ({ day, outsideCurrentMonth }) => ({
                onMouseEnter: (event) => {
                  if (!outsideCurrentMonth) {
                    setHoveredDate({ target: event.target, date: day.format("YYYY-MM-DD") })
                  }
                },
                onMouseLeave: () => {
                  setHoveredDate(null);
                },
              }),
            }}
            shouldDisableDate={(day) => {
              const formatted = day.format("YYYY-MM-DD");
              return imageData?.thumbnails?.[formatted] == undefined
            }}
          />
          {((mode?.views || ["Time"]).includes("Time")) && <>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TimeColumn
                title="Time Slot"
                options={hourOptions}
                activeoptions={(imageData?.times || []).map((d) => d.HHmm.split(":")[0])}
                onClick={(v) => setValue(prev => ({ ...prev, HH: v }))}
                selected={value?.HH}
                format="hh"
              />
              <TimeColumn
                title="hh:mm"
                label="HHmm"
                value="cvtDateTime"
                options={minsecs}
                onClick={(v) => { setSelectedMinSec(v); onAction("date", v) }}
                selected={selectedMinSec?.cvtDateTime}
                format="hh:mm"
              />
            </Box>
          </>}
        </Box>

        <Box display={'flex'} gap={2} alignItems={'center'} justifyContent={'end'}>
          <Box sx={boxStyle} className="hover" onClick={() => onAction("timeline")}>
            <BsFilm /> <Box>Timeline</Box>
          </Box>
          <Box sx={boxStyle} closable="true" className="hover" onClick={() => { onDateChange() }}>
            <BsImage closable="true" /> <Box closable="true">Current Image</Box>
          </Box>
        </Box>


        <Popper
          open={Boolean(hoveredDate)}
          anchorEl={hoveredDate?.target}
          placement="right-start"
          modifiers={[{ name: 'offset', options: { offset: [24, 0] } }]}
          sx={{ zIndex: 9999, background: '#ddd8d8', borderRadius: 2 }}
        >
          <Box display={'flex'} flexDirection={'column'}>


            {!hoveredDate?.date ? (
              <Typography sx={{ p: 1, fontSize: 11 }}>No Data</Typography>
            ) : (<>
              <img
                src={`${imageData.root}/${hoveredDate?.date}/${imageData?.thumbnails?.[hoveredDate?.date]?.thumbnail}`}
                alt="Preview"
                style={{ width: 100, height: 'auto', borderRadius: 3 }}
                onError={(e) => (e.target.style.display = 'none')}
              />
            </>)}

          </Box>
        </Popper>

      </Box>
    </LocalizationProvider>
  );
}
